import React, { useState, useEffect } from 'react';
import { Modal } from '../../common/Modal';
import { useDisputesStore } from '../../../stores/disputesStore';
import { Upload, X, FileText, Image as ImageIcon, File, AlertCircle } from 'lucide-react';
import { Evidence } from '../../../types/pilgrimGuide';
import { useLanguage } from '../../../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { uploadFile } from '../../../utils/firebase';
import { usePilgrimGuideStore } from '../../../stores/pilgrimGuideStore';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  disputeId: string;
  organizerId: string;
  existingEvidence?: Evidence;
};

export const EvidenceModal = ({
  isOpen,
  onClose,
  disputeId,
  organizerId,
  existingEvidence
}: Props) => {
  const { translations } = useLanguage();
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<Array<{
    url: string;
    type: 'image' | 'pdf';
    name: string;
  }>>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { submitEvidence } = useDisputesStore();
  const { refreshData } = usePilgrimGuideStore();

  // Initialize form with existing evidence when modal opens or evidence changes
  useEffect(() => {
    if (isOpen) {
      console.log('Modal opened, initializing with evidence:', existingEvidence);
      
      if (existingEvidence) {
        // Set text evidence
        setText(existingEvidence.text || '');

        // Set files
        if (existingEvidence.files && Array.isArray(existingEvidence.files)) {
          setExistingFiles(existingEvidence.files);
        } else {
          setExistingFiles([]);
        }
      } else {
        // Reset form for new submission
        setText('');
        setFiles([]);
        setExistingFiles([]);
      }
    }
  }, [isOpen, existingEvidence]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validFiles = selectedFiles.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
      
      if (!isValidType) {
        toast.error(translations?.evidenceModal?.invalidFileType || 'Only images and PDFs are allowed');
      }
      if (!isValidSize) {
        toast.error(translations?.evidenceModal?.fileTooLarge || 'File size must be less than 5MB');
      }
      
      return isValidType && isValidSize;
    });

    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeNewFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingFile = (index: number) => {
    setExistingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!text.trim()) {
      toast.error(translations?.evidenceModal?.textRequired || 'Evidence text is required');
      return;
    }

    setIsUploading(true);
    try {
      // Upload new files
      const newFileUrls = await Promise.all(
        files.map(file => uploadFile(file))
      );

      // Combine existing and new files
      const allFiles = [...existingFiles, ...newFileUrls];

      // Submit evidence with all files
      await submitEvidence(disputeId, organizerId, {
        text: text.trim(),
        files: allFiles
      });

      // Refresh links data
      await refreshData();

      toast.success(translations?.evidenceModal?.submitSuccess || 'Evidence submitted successfully');
      onClose();
    } catch (error: any) {
      console.error('Error submitting evidence:', error);
      toast.error(error.message || translations?.evidenceModal?.submitError || 'Failed to submit evidence');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={existingEvidence 
        ? (translations?.evidenceModal?.editTitle || 'Edit Evidence')
        : (translations?.evidenceModal?.submitTitle || 'Submit Evidence')}
    >
      <div className="space-y-6">
        {/* Text Evidence */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {translations?.evidenceModal?.textLabel || 'Evidence Text'}
            <span className="text-red-500 mr-1">*</span>
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            placeholder={translations?.evidenceModal?.textPlaceholder || 'Enter evidence details here...'}
            required
          />
        </div>

        {/* Existing Files */}
        {existingFiles.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">
              {translations?.evidenceModal?.existingFiles || 'Existing Files'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {existingFiles.map((file, index) => (
                <motion.div
                  key={`existing-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="relative group"
                >
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    {file.type === 'image' ? (
                      <div className="relative">
                        <img
                          src={file.url}
                          alt={file.name}
                          className="w-full h-48 object-cover rounded-lg border"
                        />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 rounded-lg">
                          <div className="text-white px-4 py-2">
                            {translations?.evidenceModal?.viewImage || 'View Image'}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <File size={24} className="text-red-500" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {file.name}
                          </p>
                          <p className="text-sm text-gray-500">PDF Document</p>
                        </div>
                      </div>
                    )}
                  </a>
                  <button
                    onClick={() => removeExistingFile(index)}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={14} />
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* New Files */}
        {files.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">
              {translations?.evidenceModal?.newFiles || 'New Files'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {files.map((file, index) => (
                <motion.div
                  key={`new-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="relative group"
                >
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {file.type.startsWith('image/') ? (
                        <ImageIcon size={24} className="text-blue-500" />
                      ) : (
                        <File size={24} className="text-red-500" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeNewFile(index)}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={14} />
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* File Upload */}
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-500 transition-colors">
          <div className="space-y-1 text-center">
            <Upload
              className={`mx-auto h-12 w-12 ${
                isUploading ? 'animate-bounce text-primary-500' : 'text-gray-400'
              }`}
            />
            <div className="flex text-sm text-gray-600">
              <label className="relative cursor-pointer rounded-md font-medium text-primary-600 hover:text-primary-500">
                <span>{translations?.evidenceModal?.uploadButton || 'Upload Files'}</span>
                <input
                  type="file"
                  className="sr-only"
                  accept="image/*,.pdf"
                  multiple
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
              </label>
              <p className="pl-1">
                {translations?.evidenceModal?.dragAndDrop || 'or drag and drop'}
              </p>
            </div>
            <p className="text-xs text-gray-500">
              {translations?.evidenceModal?.fileRequirements || 'Images and PDFs up to 5MB'}
            </p>
          </div>
        </div>

        {/* Warning Message */}
        {existingEvidence && (
          <div className="flex items-start gap-2 bg-yellow-50 text-yellow-800 p-4 rounded-lg">
            <AlertCircle className="shrink-0 mt-0.5" size={20} />
            <p className="text-sm">
              {translations?.evidenceModal?.editWarning || 
                'Editing your evidence will update your existing submission. Make sure to include all necessary files and information.'}
            </p>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.evidenceModal?.cancelButton || 'Cancel'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isUploading || !text.trim()}
            className={`px-6 py-2 rounded-lg text-white transition-colors ${
              isUploading || !text.trim()
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary-500 hover:bg-primary-600'
            }`}
          >
            {isUploading
              ? translations?.evidenceModal?.savingButton || 'Saving...'
              : existingEvidence
              ? translations?.evidenceModal?.updateButton || 'Update Evidence'
              : translations?.evidenceModal?.submitButton || 'Submit Evidence'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
